/**
 * @file   src/components/AiChat/AiChatInput.js
 * @brief  Auto-expanding textarea with send button for AI chat
 * @date   February, 2026
 * @author Jake Hokinson
 */
import React, { useRef, useCallback } from "react";

const AiChatInput = ({ value, onChange, onSend, disabled, placeholder }) => {
  const textareaRef = useRef(null);

  const handleInput = useCallback((e) => {
    onChange(e.target.value);
    // Auto-expand textarea
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 144) + "px"; // max ~6 lines
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSend();
        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
      }
    }
  }, [value, disabled, onSend]);

  const handleSendClick = useCallback(() => {
    if (value.trim() && !disabled) {
      onSend();
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  }, [value, disabled, onSend]);

  return (
    <div className="ai-chat-input-area">
      <div className={`ai-chat-input-wrap ${disabled ? "disabled" : ""}`}>
        <textarea
          ref={textareaRef}
          className="ai-chat-textarea"
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Describe the property your client wants..."}
          disabled={disabled}
          rows={1}
        />
        <button
          className="ai-chat-send-btn"
          onClick={handleSendClick}
          disabled={!value.trim() || disabled}
          title="Send message"
        >
          ↑
        </button>
      </div>
    </div>
  );
};

export default AiChatInput;
